// React and hooks
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

// Material UI
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
// Redux actions
import {
  getAllPatientByEnterprise,
  getAllPhysicianDocumentsAction,
  getFacility,
  resetAllPatientReducer,
  resetGetAllFacility,
  resetPhysicianSignDoc,
  resetUpdateDocumentStatuses,
  updateDocumentStatusesAction,
} from 'src/redux/actions/physicianActions';
import { previewDashboardDoc } from 'src/redux/actions/therapist-dashboard-action';

// Types and interfaces
import physicianService, {
  PhysicianDTO,
  PhysicianDocument,
  DocumentSignatureWorkflowStatuses,
  initialPhysicianDTO,
  compositeWorkflowStatusFromDocument,
} from 'src/services/apiServices/physician-service/physician-service';
import { OptionType, SortOptions } from './types/SortOptions';
import { physicianConst } from './table-heads';

// Local components
import { Scrollbar } from '../scrollbar';
import { DocumentTableRow } from './components/DocumentTableRow';
import { DocumentTableHeader } from './components/DocumentTableHeader';
import { DocumentTableLoader } from './components/DocumentTableLoader';
import { DocumentTableEndMessage } from './components/DocumentTableEndMessage';
import SignatureModal from './components/SignatureModal';
import SigningLockedDialog from 'src/components/signature/signing-locked-dialog';

type PhysicianDocumentsTableType = {
  source: string;
  signedDocumentIds?: string[];
};

function PhysicianDocumentsTableV2({
  source,
  signedDocumentIds = [],
}: PhysicianDocumentsTableType) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const statuses_pending_approval = [
    'PENDING_APPROVAL',
    'OVERDUE',
    'URGENT',
    'UPCOMING',
    'NEEDS APPROVED',
  ];
  const statuses_approved = ['APPROVED'];
  const statuses_signed = ['SIGNED'];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [physicianDto, setPhysicianDto] = useState<PhysicianDTO>({
    ...initialPhysicianDTO,
    page: 0,
    pageSize: 10,
  });
  const lastApiCallRef = useRef<string>('');
  const [selectedDocuments, setSelectedDocuments] = useState<PhysicianDocument[]>([]);
  const [pendingApprovalDocuments, setPendingApprovalDocuments] = useState<string[]>([]);
  const [approvedDocuments, setApprovedDocuments] = useState<string[]>([]);
  const [documentsNeedingApprovalCountPerPage, setDocumentsNeedingApprovalCountPerPage] = useState<Map<number, number>>(new Map());
  const [selectedDueDate, setSelectedDueDate] = useState<any>({
    startDate: '',
    endDate: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
  });
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [locked, setLocked] = useState<{ open: boolean; until: string | null }>({
    open: false,
    until: null,
  });
  const [isSigningDocuments, setIsSigningDocuments] = useState(false);

  const user = useSelector((state: any) => state.getTherapistProfileReducer?.response?.data);
  const allDocumentsOfPhysician = useSelector((state: any) => state.physicianDocumentsReducer);
  const getAllFacilities = useSelector((state: any) => state.getFacilityReducer);
  const getAllPatient = useSelector((state: any) => state.getAllPatientByEnterpriseReducer);
  const updateDocumentStatuses = useSelector((state: any) => state.updateDocumentStatusesReducer);

  const getListOfValues = (items: any) => {
    return items?.map((therapist: any) => therapist?.value);
  };

  const getAllFacilityList = useCallback(
    (label: string): OptionType[] => {
      if (label === 'Patient') {
        return (
          getAllPatient?.totalPatient?.map((res: any) => ({
            label: `${res?.lastName}, ${res?.firstName}`,
            value: res?.patientId,
          })) || []
        );
      }
      if (label === 'Facility Name') {
        return (
          getAllFacilities?.totalFacility?.map((res: any) => ({
            label: `${res?.name}`,
            value: res?.uuid,
          })) || []
        );
      }
      return [];
    },
    [getAllFacilities, getAllPatient]
  );

  const getTheDates = (startDate: any, endDate: any) => {
    return {
      startDate,
      endDate,
    };
  };

  const handleCallTheList = useCallback((label: any) => {
    if (label === 'Patient') {
      dispatch(resetAllPatientReducer());
      dispatch(
        getAllPatientByEnterprise({
          searchString: '',
          modified: 'asc',
          page: 0,
          pageSize: 20,
        })
      );
      return;
    }
    // getAllPatientByEnterprise
    if (label === 'Facility Name') {
      dispatch(resetGetAllFacility());
      dispatch(
        getFacility({
          page: 0,
          searchString: '',
          pageSize: 20,
        })
      );
    }
  }, []);

  const handleSort = (sortOption: SortOptions) => {
    const key = sortOption.key;
    const old_direction = sortOption.direction || 'desc';
    const direction = old_direction === 'asc' ? 'desc' : 'asc';

    if (key === '') return;
    // Reset cumulative count when sorting changes
    setDocumentsNeedingApprovalCountPerPage(new Map());
    setPage(0);
    setPhysicianDto((prevState: any) => ({
      ...prevState,
      page: 0,
      pageSize: rowsPerPage,
      sortBy: key,
      sortDirection: direction,
    }));
  };

  const handleSortMultiselect = useCallback((label: any, direction: any) => {
    // Reset cumulative count when filter changes
    setDocumentsNeedingApprovalCountPerPage(new Map());
    setPage(0);
    setPhysicianDto((prevState: any) => ({
      ...prevState,
      page: 0,
      pageSize: rowsPerPage,
      sortBy:
        label === 'Facility Name'
          ? 'facilityName'
          : label === 'Patient'
          ? 'patientName'
          : label === 'Document Type'
          ? 'documentType'
          : '',
      sortDirection: direction,
    }));
  }, [rowsPerPage]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    // The useEffect will handle the API call when page changes
    // The documentsNeedingApprovalCountPerPage will be updated when the new page data loads
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // The useEffect will handle the API call when rowsPerPage changes
  };

  const handleCallNextApi = (label: any, searchValue: any, isFromSearch: any) => {
    if (label === 'Facility Name') {
      isFromSearch && dispatch(resetGetAllFacility());
      dispatch(
        getFacility({
          page: getAllFacilities?.pageNumber,
          searchString: searchValue,
          pageSize: 20,
        })
      );
    }
    if (label === 'Patient') {
      isFromSearch && dispatch(resetAllPatientReducer());
      dispatch(
        getAllPatientByEnterprise({
          searchString: searchValue,
          modified: 'asc',
          page: getAllPatient?.pageNumber,
          pageSize: 20,
        })
      );
    }
  };

  const previewDocument = (formId: any) => {
    const { documentUrl, ...restDoc } = formId;
    dispatch(previewDashboardDoc({ ...restDoc, pdfUrl: documentUrl }));
    navigate('/physician-dashboard/doc', {
      state: {
        source: 'PHYSICIAN',
      },
    });
  };

  const handleSelect = (document: PhysicianDocument, checked: boolean) => {
    setSelectedDocuments((prev) => {
      const status = compositeWorkflowStatusFromDocument(document);
      
      if (checked && statuses_signed.includes(status)) {
        setSnackbar({
          open: true,
          message: 'Document is already signed. Please select another document to approve',
        });
        return prev;
      }

      if (checked && statuses_approved.includes(status)) {
        setSnackbar({
          open: true,
          message: 'Document is already approved. Please select another document to approve',
        });
        return prev;
      }

      if (checked) {
        // Check if document is already selected
        if (prev.some((doc) => doc.uuid === document.uuid)) {
          return prev;
        }
        return [...prev, document];
      } else {
        return prev.filter((doc) => doc.uuid !== document.uuid);
      }
    });
  };

  const handleBulkSignature = useCallback(() => {
    if (!user?.providerId || approvedDocuments.length === 0) return;
    setIsSignModalOpen(true);
  }, [user?.providerId, approvedDocuments]);

  const handleSignConfirm = useCallback(
    async (signatureName: string, consentConfirmation: boolean, pin?: string) => {
      if (!user?.providerId) return;

      setPinError(null);
      setIsSigningDocuments(true);
      try {
        const documentsToSign = [...approvedDocuments];
        //  THIN-7681: thread the signing PIN. Do NOT optimistically close/clear here — that made a
        //  failed (wrong-PIN) sign look successful. The success effect handles close + cleanup; a
        //  PIN-challenge error keeps the modal open for a retry.
        dispatch(
          updateDocumentStatusesAction(
            user.providerId,
            documentsToSign,
            'SIGNED',
            signatureName,
            pin,
            consentConfirmation
          )
        );
      } catch (error) {
        setIsSigningDocuments(false);
        setSnackbar({
          open: true,
          message: 'Error signing documents. Please try again later.',
        });
      }
    },
    [user?.providerId, approvedDocuments, dispatch]
  );

  const handleApprovalNavigation = (document: any) => {
    navigateToApprovalSignature();
  };

  const navigateToApprovalSignature = useCallback(() => {
    navigate('/physician-dashboard/documents-sign', {
      state: {
        selectedDocuments,
        source: 'PHYSICIAN',
      },
    });
  }, [navigate, selectedDocuments]);

  // Reset reducer on mount to clear any accumulated data from infinite scroll
  useEffect(() => {
    if (user?.providerId) {
      dispatch(resetPhysicianSignDoc());
    }
  }, [user?.providerId]);

  useEffect(() => {
    if (!user?.providerId) return;
    const { patientIds, facilityIds, type, page: dtoPage, pageSize, ...resetPhysicianDto } = physicianDto;
    const updatedPhysicianDto = {
      patientIds: getListOfValues(patientIds),
      facilityIds: getListOfValues(facilityIds),
      signatureStatus: source,
      type: getListOfValues(type),
      page: page, // Use state page, not physicianDto.page
      pageSize: rowsPerPage, // Use state rowsPerPage, not physicianDto.pageSize
      ...resetPhysicianDto,
    };
    
    // Create a unique key for this API call to prevent duplicates
    const apiCallKey = JSON.stringify({
      page: updatedPhysicianDto.page,
      pageSize: updatedPhysicianDto.pageSize,
      sortBy: updatedPhysicianDto.sortBy,
      sortDirection: updatedPhysicianDto.sortDirection,
      patientIds: updatedPhysicianDto.patientIds,
      facilityIds: updatedPhysicianDto.facilityIds,
      type: updatedPhysicianDto.type,
      signatureStatus: updatedPhysicianDto.signatureStatus,
    });
    
    // Skip if this is the same API call as the last one
    if (lastApiCallRef.current === apiCallKey) {
      return;
    }
    
    lastApiCallRef.current = apiCallKey;
    dispatch(getAllPhysicianDocumentsAction(user?.providerId, updatedPhysicianDto));
  }, [physicianDto, page, rowsPerPage, user?.providerId, source]);

  // Refresh pending documents when documents are successfully signed
  // Handle successful sign operation
  useEffect(() => {
    if (
      updateDocumentStatuses?.isUpdateSuccess &&
      source === physicianConst.PENDING &&
      user?.providerId
    ) {
      // Clear loading state when operation completes
      setIsSigningDocuments(false);
      setIsSignModalOpen(false);
      setApprovedDocuments([]);

      // After successful update, refresh the document list
      // The selectedDocuments will be cleaned up in the other useEffect based on current statuses
      // Reset the cumulative count when documents are signed/approved
      setDocumentsNeedingApprovalCountPerPage(new Map());
      setPage(0);
      const { patientIds, facilityIds, type, page, pageSize, ...resetPhysicianDto } = physicianDto;
      const updatedPhysicianDto = {
        patientIds: getListOfValues(patientIds),
        facilityIds: getListOfValues(facilityIds),
        signatureStatus: source,
        type: getListOfValues(type),
        page: 0,
        pageSize: rowsPerPage,
        ...resetPhysicianDto,
      };
      dispatch(resetPhysicianSignDoc());
      dispatch(getAllPhysicianDocumentsAction(user.providerId, updatedPhysicianDto));
      dispatch(resetUpdateDocumentStatuses());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDocumentStatuses?.isUpdateSuccess, source, user?.providerId, dispatch]);

  // Handle failed sign operation
  useEffect(() => {
    if (
      updateDocumentStatuses?.response &&
      !updateDocumentStatuses?.isUpdateSuccess &&
      updateDocumentStatuses?.response?.code !== 'OK' &&
      isSigningDocuments
    ) {
      //  THIN-7681: surface a signing-PIN challenge failure — keep the modal open to retry
      //  (WRONG_PIN / NO_PIN_SET) or show the lockout (LOCKED).
      const challenge = updateDocumentStatuses?.response?.data;
      const outcome = challenge?.outcome;
      if (outcome === 'LOCKED') {
        setLocked({ open: true, until: challenge?.lockedUntil ?? null });
        setIsSignModalOpen(false);
      } else if (outcome === 'WRONG_PIN') {
        const left = challenge?.attemptsRemaining;
        setPinError(
          `Incorrect PIN.${left != null ? ` ${left} attempt${left === 1 ? '' : 's'} remaining.` : ''}`
        );
      } else if (outcome === 'NO_PIN_SET') {
        setPinError('No signing PIN set yet. Set one up in Profile → Signature & PIN.');
      }
      // Clear loading state on failure
      setIsSigningDocuments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDocumentStatuses?.response, updateDocumentStatuses?.isUpdateSuccess]);

  // Show warning toast when one or more documents failed to sign
  const lastBulkSignWarningRef = useRef<string | null>(null);
  useEffect(() => {
    const response = updateDocumentStatuses?.response;
    if (!response) {
      lastBulkSignWarningRef.current = null;
      return;
    }
    const data = response?.data;
    const hasFailure = Array.isArray(data)
      ? data.some((d: any) => d?.isSuccessful === false)
      : data?.isSuccessful === false;
    if (hasFailure && response?.message) {
      const key = `${response?.requestId ?? ''}|${response?.message}`;
      if (lastBulkSignWarningRef.current !== key) {
        lastBulkSignWarningRef.current = key;
        toast(response.message, { icon: '⚠️', position: 'top-right' });
      }
    }
  }, [updateDocumentStatuses?.response]);

  useEffect(() => {
    const currentDocuments = allDocumentsOfPhysician?.response?.data?.content || [];
    if (currentDocuments?.length > 0) {
      const pendingDocs = currentDocuments
        .filter((doc: PhysicianDocument) =>
          statuses_pending_approval.includes(compositeWorkflowStatusFromDocument(doc))
        )
        .map((doc: PhysicianDocument) => doc.uuid);
      setPendingApprovalDocuments(pendingDocs);

      // Count documents needing approval on current page (not approved, not signed)
      const currentPageNumber = page;
      const documentsNeedingApprovalOnCurrentPage = currentDocuments.filter((doc: PhysicianDocument) => {
        if (doc.status === 'COMPLETED') return false;
        const status = compositeWorkflowStatusFromDocument(doc);
        return !statuses_approved.includes(status) && !statuses_signed.includes(status);
      }).length;

      // Update cumulative count per page
      setDocumentsNeedingApprovalCountPerPage((prevMap) => {
        const newMap = new Map(prevMap);
        newMap.set(currentPageNumber, documentsNeedingApprovalOnCurrentPage);
        return newMap;
      });

      // Track approved documents across all pages
      const approvedDocsFromCurrentPage = currentDocuments
        .filter((doc: PhysicianDocument) =>
          statuses_approved.includes(compositeWorkflowStatusFromDocument(doc))
        )
        .map((doc: PhysicianDocument) => doc.uuid);
      
      setApprovedDocuments((prevApproved) => {
        // Remove approved docs that are no longer on current page (might have been signed)
        const filteredPrev = prevApproved.filter((uuid) => {
          const currentDoc = currentDocuments.find((d: PhysicianDocument) => d.uuid === uuid);
          if (currentDoc) {
            const status = compositeWorkflowStatusFromDocument(currentDoc);
            return statuses_approved.includes(status);
          }
          // Keep approved docs from other pages
          return true;
        });
        
        // Add new approved docs from current page
        const newApprovedUuids = approvedDocsFromCurrentPage.filter(
          (uuid: string) => !filteredPrev.includes(uuid)
        );
        
        return [...filteredPrev, ...newApprovedUuids];
      });

      // Preserve selections across pages and auto-select all selectable documents by default
      setSelectedDocuments((prevSelected) => {
        // First, remove documents that are no longer selectable (approved or signed)
        const filteredPrev = prevSelected.filter((doc) => {
          const currentDoc = currentDocuments.find((d: PhysicianDocument) => d.uuid === doc.uuid);
          if (currentDoc) {
            const status = compositeWorkflowStatusFromDocument(currentDoc);
            return !statuses_approved.includes(status) && !statuses_signed.includes(status);
          }
          // Keep documents that are not on the current page (they might be on other pages)
          return true;
        });

        const docsFromCurrentPage = currentDocuments.filter((doc: PhysicianDocument) => {
          const status = compositeWorkflowStatusFromDocument(doc);
          return !statuses_approved.includes(status) && !statuses_signed.includes(status);
        });

        // Get UUIDs of already selected documents
        const selectedUuids = new Set(filteredPrev.map((doc) => doc.uuid));

        // Add new documents from current page that aren't already selected
        const newDocsToAdd = docsFromCurrentPage.filter(
          (doc: PhysicianDocument) => !selectedUuids.has(doc.uuid)
        );

        // Combine existing selections with new documents from current page
        return [...filteredPrev, ...newDocsToAdd];
      });
    } else if ((allDocumentsOfPhysician?.response?.data?.totalElements ?? 0) === 0) {
      //  Refetch returned no pending documents at all — reconcile the cross-page
      //  selection/approval state. The reconcile logic above is gated on a non-empty
      //  page, so without this an empty result leaves stale counters and the action
      //  buttons show phantom counts (e.g. "Approve Selected (19)" / "Sign Approved (1)")
      //  over a "No Records Found" table.
      setPendingApprovalDocuments([]);
      setSelectedDocuments([]);
      setApprovedDocuments([]);
      setDocumentsNeedingApprovalCountPerPage(new Map());
    }
  }, [allDocumentsOfPhysician?.response?.data?.content]);


  const filterProps = {
    getAllFacilityList,
    getAllPatient,
    getTheDates,
    handleCallNextApi,
    handleCallTheList,
    handleSortMultiselect,
    selectedDueDate,
    setSelectedDueDate,
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 2 }}>
          <Typography variant="h5">Signature Pending</Typography>
          <Chip
            label={`for ${
              (() => {
                // Calculate cumulative count of documents needing approval from pages up to current page
                let cumulativeCount = 0;
                documentsNeedingApprovalCountPerPage.forEach((count, pageNumber) => {
                  // Only include pages up to and including the current page
                  if (pageNumber <= page) {
                    cumulativeCount += count;
                  }
                });
                // Cap at totalElements to avoid showing more than total
                const totalElements = allDocumentsOfPhysician?.response?.data?.totalElements || 0;
                return Math.min(cumulativeCount, totalElements);
              })()
            } / ${allDocumentsOfPhysician?.response?.data?.totalElements || 0} Documents`}
            color="primary"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, pr: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApprovalNavigation}
            disabled={selectedDocuments.length === 0}
          >
            Approve Selected Documents ({selectedDocuments.length})
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkSignature}
            disabled={approvedDocuments.length === 0 || isSigningDocuments}
            startIcon={isSigningDocuments ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Sign Approved Documents ({approvedDocuments.length})
          </Button>
        </Box>
      </Box>

      <SignatureModal
        open={isSignModalOpen}
        onClose={() => {
          setIsSignModalOpen(false);
          setPinError(null);
        }}
        onSign={handleSignConfirm}
        signatureName={signatureName}
        setSignatureName={setSignatureName}
        pinError={pinError}
      />
      <SigningLockedDialog
        open={locked.open}
        lockedUntil={locked.until}
        onClose={() => setLocked({ open: false, until: null })}
      />

      <Scrollbar>
        <Stack gap="1rem">
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }}>
              <DocumentTableHeader
                physicianDto={physicianDto}
                setPhysicianDto={setPhysicianDto}
                onSort={handleSort}
                getAllFacilityList={getAllFacilityList}
                getAllPatient={getAllPatient}
                getTheDates={getTheDates}
                handleCallNextApi={handleCallNextApi}
                handleCallTheList={handleCallTheList}
                handleSortMultiselect={handleSortMultiselect}
                selectedDueDate={selectedDueDate}
                setSelectedDueDate={setSelectedDueDate}
                facilitiesCount={getAllFacilities?.response?.data?.data?.totalElements}
                patientsCount={getAllPatient?.response?.data?.data?.totalElements}
              />
              <TableBody>
                {allDocumentsOfPhysician?.loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <DocumentTableLoader allDocumentsOfPhysician={allDocumentsOfPhysician} />
                    </TableCell>
                  </TableRow>
                ) : (() => {
                  const filteredDocuments = (allDocumentsOfPhysician?.response?.data?.content || [])
                    .filter((document: any) => {
                      // Filter out COMPLETED documents
                      if (document.status === 'COMPLETED') return false;
                      // Filter out SIGNED documents (they should appear in COMPLETED DOCUMENTS list)
                      const status = compositeWorkflowStatusFromDocument(document);
                      if (statuses_signed.includes(status)) return false;
                      // Keep APPROVED documents - they still need to be signed, so show them in SIGNATURE PENDING
                      return true;
                    });

                  return filteredDocuments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box
                          textAlign="center"
                          p={3}
                        >
                          <Typography
                            color={'text.secondary'}
                            variant="subtitle2"
                          >
                            No Records Found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDocuments.map((document: any) => (
                      <DocumentTableRow
                        key={document.uuid}
                        document={document}
                        onPreview={previewDocument}
                        selected={selectedDocuments.some((doc) => doc.uuid === document.uuid)}
                        onSelect={handleSelect}
                        isSignedExternally={signedDocumentIds.includes(document.uuid)}
                      />
                    ))
                  );
                })()}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={allDocumentsOfPhysician?.response?.data?.totalElements || 0}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </TableContainer>
        </Stack>
      </Scrollbar>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PhysicianDocumentsTableV2;
